import { motion } from "framer-motion";
import { ArrowRight, Sparkles, TrendingUp, Users, Building2 } from "lucide-react";
import { Button } from "../ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/jd65gb5i_CityWorth%20Logo.png";

const TECH_IMAGE = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/ssan3r0n_Untitled-design-61.png";

const HeroSection = () => {
  const scrollToApply = () => {
    document.getElementById("apply-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const fadeUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  return (
    <section className="hero-bg-light min-h-screen relative overflow-hidden" data-testid="hero-section">
      {/* Grid pattern background */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      
      {/* Floating accent shapes */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-red-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
      
      <div className="container-main relative z-10 pt-8 pb-20 min-h-screen flex flex-col">
        {/* Navigation */}
        <nav className="flex items-center justify-between py-4">
          <motion.img
            src={LOGO_URL}
            alt="CityWorth"
            className="h-10 md:h-12"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            data-testid="logo"
          />
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Button
              onClick={scrollToApply}
              className="btn-primary px-6 py-2.5 rounded-full font-semibold text-white"
              data-testid="nav-apply-btn"
            >
              Apply Now
            </Button>
          </motion.div>
        </nav>

        {/* Hero Content */}
        <motion.div
          className="flex-1 flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-16 mt-8 lg:mt-0"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {/* Left Content */}
          <div className="flex-1 max-w-2xl text-center lg:text-left">
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-50 border border-red-100 mb-6"
            >
              <Sparkles className="w-4 h-4 text-red-500" />
              <span className="text-sm font-medium text-red-700">
                The Future of Real Estate Careers
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] mb-6 text-slate-900"
            >
              Join the{" "}
              <span className="gradient-text">Revolution</span>
              <br />
              in Homebuying
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0"
            >
              Imagine being one of the first 100 employees at a company like Apple, Amazon, or Tesla that completely changed their industry. CityWorth is doing just that—and now is your opportunity to get in with a company set out to change real estate as we know it.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Button
                onClick={scrollToApply}
                size="lg"
                className="btn-primary px-8 py-6 rounded-full text-lg font-semibold text-white"
                data-testid="hero-apply-btn"
              >
                Start Your Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => document.getElementById("role-overview")?.scrollIntoView({ behavior: "smooth" })}
                className="px-8 py-6 rounded-full text-lg font-semibold border-slate-300 hover:bg-slate-50 text-slate-700"
                data-testid="hero-learn-more-btn"
              >
                Learn More
              </Button>
            </motion.div>

            {/* Quick stats */}
            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap gap-8 mt-12 justify-center lg:justify-start"
            >
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold text-slate-900">No</div>
                <div className="text-sm text-slate-500">Cold Calling</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold text-slate-900">No</div>
                <div className="text-sm text-slate-500">Open Houses</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-bold text-slate-900">Pre-Approved</div>
                <div className="text-sm text-slate-500">Leads Provided</div>
              </div>
            </motion.div>
          </div>

          {/* Right Content - Image */}
          <motion.div
            className="flex-1 relative hidden lg:flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="relative w-full max-w-lg">
              {/* Main image */}
              <div className="image-card rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={TECH_IMAGE} 
                  alt="Professional using advanced technology platform"
                  className="w-full h-80 object-cover"
                />
              </div>

              {/* Floating badge */}
              <motion.div
                className="absolute -top-4 -right-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100"
                animate={{ y: [0, -8, 0], rotate: [0, 2, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
              >
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-slate-700">Full Support Team</span>
                </div>
              </motion.div>

              {/* Stats badge */}
              <motion.div
                className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-lg border border-slate-100"
                animate={{ y: [0, -6, 0], rotate: [0, -2, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-medium text-slate-700">Nationwide Vision</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-slate-300 flex justify-center pt-2">
            <div className="w-1.5 h-3 rounded-full bg-slate-400" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
